import { useEffect, useState, useCallback, useRef } from "react";
import { useStytchB2BClient } from "@stytch/react/b2b";
import { WebSocketClient, WebSocketMessage } from "../utils/websocket";

type UseWebSocketOptions = {
  autoConnect?: boolean;
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
};

type UseWebSocketReturn = {
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
};

export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const { autoConnect = true, onMessage, onConnect, onDisconnect, onError } = options;

  const stytch = useStytchB2BClient();
  const [isConnected, setIsConnected] = useState(false);
  const clientRef = useRef<WebSocketClient | null>(null);

  const callbacksRef = useRef({ onMessage, onConnect, onDisconnect, onError });
  callbacksRef.current = { onMessage, onConnect, onDisconnect, onError };

  const stytchRef = useRef(stytch);
  stytchRef.current = stytch;

  const wsUrl = import.meta.env.VITE_WEBSOCKET_URL;

  useEffect(() => {
    if (!wsUrl) {
      console.error("VITE_WEBSOCKET_URL environment variable is not set");
      return;
    }

    clientRef.current = new WebSocketClient({
      url: wsUrl,
      getToken: () => {
        const token = stytchRef.current.session.getTokens()?.session_token;
        if (!token) {
          console.warn("No Stytch session token available");
        }
        return token;
      },
      onMessage: (message) => {
        callbacksRef.current.onMessage?.(message);
      },
      onConnect: () => {
        setIsConnected(true);
        callbacksRef.current.onConnect?.();
      },
      onDisconnect: () => {
        setIsConnected(false);
        callbacksRef.current.onDisconnect?.();
      },
      onError: (error) => {
        console.error("WebSocket error:", error);
        callbacksRef.current.onError?.(error);
      },
      reconnectInterval: 3000,
      maxReconnectAttempts: 10,
    });

    if (autoConnect) {
      clientRef.current.connect();
    }

    return () => {
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsUrl, autoConnect]);

  const connect = useCallback(() => {
    if (!clientRef.current) {
      console.warn("WebSocket client not initialized");
      return;
    }
    clientRef.current.connect();
  }, []);

  const disconnect = useCallback(() => {
    if (!clientRef.current) {
      console.warn("WebSocket client not initialized");
      return;
    }
    clientRef.current.disconnect();
  }, []);

  return {
    isConnected,
    connect,
    disconnect,
  };
}
